'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Script from 'next/script';
import { ChevronRight, MapPin, CheckCircle, ArrowRight, Upload, CreditCard, Zap, Shield } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

// ─── Plan definitions ────────────────────────────────────────────────────────
interface Plan {
  id: string;
  slug: string;
  name: string;
  amount: number;
  duration_days: number;
  description: string;
  features: string[];
  isFeatured: boolean;
  badge?: string;
}

const PLANS: Plan[] = [
  {
    id: 'free',
    slug: 'free',
    name: 'Free Listing',
    amount: 0,
    duration_days: 30,
    description: 'Basic listing with standard visibility',
    features: ['30-day listing', 'Standard placement', 'Up to 5 photos', 'Basic enquiries'],
    isFeatured: false,
  },
  {
    id: 'premium',
    slug: 'premium',
    name: 'Premium Listing',
    amount: 499,
    duration_days: 60,
    description: 'Higher visibility with featured badge',
    features: ['60-day listing', 'Featured badge', 'Up to 20 photos', 'Priority placement', 'Unlimited enquiries'],
    isFeatured: true,
    badge: 'Popular',
  },
  {
    id: 'elite',
    slug: 'elite',
    name: 'Elite Listing',
    amount: 999,
    duration_days: 90,
    description: 'Maximum exposure across the platform',
    features: ['90-day listing', 'Top of search results', 'Unlimited photos', 'Homepage feature', 'WhatsApp leads', 'Verified badge'],
    isFeatured: true,
    badge: 'Best Value',
  },
];

const propertyTypes = ['Apartment', 'Villa', 'Plot / Land', 'Commercial', 'PG / Hostel', 'Office Space'];
const statusOptions = ['For Sale', 'For Rent', 'Lease'];

type WizardStep = 'details' | 'location' | 'photos' | 'pricing' | 'plan' | 'payment' | 'success';

const STEP_ORDER: WizardStep[] = ['details', 'location', 'photos', 'pricing', 'plan', 'payment', 'success'];

const STEP_LABELS: Record<WizardStep, string> = {
  details: 'Property Details',
  location: 'Location',
  photos: 'Photos',
  pricing: 'Pricing',
  plan: 'Choose Plan',
  payment: 'Payment',
  success: 'Done',
};

export default function PostListingWizard() {
  const { user } = useAuth();
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState<WizardStep>('details');
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [listingId, setListingId] = useState<string | null>(null);
  const [razorpayScriptLoaded, setRazorpayScriptLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    type: '',
    status: 'For Sale',
    beds: '',
    baths: '',
    area: '',
    price: '',
    description: '',
    address: '',
    city: 'Bengaluru',
  });

  const update = (key: string, val: string) => setFormData(prev => ({ ...prev, [key]: val }));

  const visibleSteps: WizardStep[] = selectedPlan?.amount === 0
    ? ['details', 'location', 'photos', 'pricing', 'plan', 'success']
    : ['details', 'location', 'photos', 'pricing', 'plan', 'payment', 'success'];

  const currentStepIndex = visibleSteps.indexOf(currentStep);

  const goNext = () => {
    const nextIdx = currentStepIndex + 1;
    if (nextIdx < visibleSteps.length) setCurrentStep(visibleSteps[nextIdx]);
  };

  const goPrev = () => {
    const prevIdx = currentStepIndex - 1;
    if (prevIdx >= 0) setCurrentStep(visibleSteps[prevIdx]);
  };

  // ─── Submit listing to DB ─────────────────────────────────────────────────
  const submitListing = async (isFree: boolean): Promise<string | null> => {
    if (!user) return null;
    try {
      const res = await fetch('/api/listings/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, formData, isFree }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      return data.listingId;
    } catch (err: any) {
      setPaymentError(err.message || 'Failed to submit listing');
      return null;
    }
  };

  // ─── Handle plan selection & proceed ─────────────────────────────────────
  const handlePlanContinue = async () => {
    if (!selectedPlan) return;
    setIsSubmitting(true);
    setPaymentError('');

    if (selectedPlan.amount === 0) {
      // Free listing — submit directly and go to success
      const id = await submitListing(true);
      if (id) {
        setListingId(id);
        setCurrentStep('success');
      }
    } else {
      // Paid listing — create listing as draft first, then go to payment
      const id = await submitListing(false);
      if (id) {
        setListingId(id);
        setCurrentStep('payment');
      }
    }
    setIsSubmitting(false);
  };

  // ─── Razorpay payment handler ─────────────────────────────────────────────
  const handleRazorpayPayment = async () => {
    if (!selectedPlan || !listingId || !user) return;
    setIsSubmitting(true);
    setPaymentError('');

    try {
      // Create Razorpay order
      const orderRes = await fetch('/api/razorpay/order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: selectedPlan.amount,
          receipt: `np_${listingId.slice(0, 8)}`,
          listingId,
          planId: selectedPlan.id !== 'free' ? selectedPlan.id : undefined,
          notes: { plan_name: selectedPlan.name, user_id: user.id },
        }),
      });

      const orderData = await orderRes.json();
      if (!orderData.success) throw new Error(orderData.error);

      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'NammaPrice',
        description: `${selectedPlan.name} - Listing Fee`,
        image: '/assets/images/app_logo.png',
        order_id: orderData.orderId,
        prefill: {
          name: user.user_metadata?.full_name || '',
          email: user.email || '',
        },
        theme: { color: '#E4191B' },
        handler: async (response: any) => {
          // Verify payment
          const verifyRes = await fetch('/api/razorpay/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              listingId,
              userId: user.id,
              planId: selectedPlan.id !== 'free' ? selectedPlan.id : undefined,
              amount: selectedPlan.amount,
            }),
          });

          const verifyData = await verifyRes.json();
          if (verifyData.success) {
            setCurrentStep('success');
          } else {
            setPaymentError(verifyData.error || 'Payment verification failed');
          }
          setIsSubmitting(false);
        },
        modal: {
          ondismiss: () => {
            setIsSubmitting(false);
          },
        },
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.on('payment.failed', async (res: any) => {
        const failureReason = res.error?.description || res.error?.reason || 'Payment failed';
        setPaymentError(`${failureReason} — Your order has been saved. You can retry payment using the link sent to your email.`);

        // Auto-save pending order and log failure
        try {
          await fetch('/api/razorpay/failure', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              razorpay_order_id: orderData.orderId,
              razorpay_payment_id: res.error?.metadata?.payment_id || null,
              error_code: res.error?.code || null,
              error_description: res.error?.description || null,
              error_reason: res.error?.reason || 'unknown',
              userId: user.id,
              listingId,
              planId: selectedPlan.id,
              amount: selectedPlan.amount,
            }),
          });
        } catch (logErr) {
          console.error('[PAYMENT_FAILURE_LOG] Failed to log:', logErr);
        }

        setIsSubmitting(false);
      });
      rzp.open();
    } catch (err: any) {
      setPaymentError(err.message || 'Payment failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  // ─── Step display labels (excluding success) ──────────────────────────────
  const displaySteps = visibleSteps.filter(s => s !== 'success');

  return (
    <div className="min-h-screen bg-[#F8F8F8] pb-20 lg:pb-0">
      <Script
        src="https://checkout.razorpay.com/v1/checkout.js"
        onLoad={() => setRazorpayScriptLoaded(true)}
      />

      {/* Page header */}
      <div className="bg-white border-b border-[#E5E7EB] py-5 lg:py-8">
        <div className="max-w-screen-lg mx-auto px-4 lg:px-6">
          <nav className="flex items-center gap-2 text-xs font-rubik text-[#777777] mb-3 lg:mb-4">
            <Link href="/" className="hover:text-[#E4191B] transition-colors">Home</Link>
            <ChevronRight size={12} />
            <span className="text-[#222222]">Post a Listing</span>
          </nav>
          <h1 className="font-poppins text-[#222222]" style={{ fontSize: 'clamp(20px, 5vw, 28px)', fontWeight: 400 }}>Post a New Listing</h1>
          <p className="font-rubik text-[#777777] mt-1 text-[13px] lg:text-[15px]" style={{ fontWeight: 300 }}>Fill in the details below to list your property on NammaPrice</p>
        </div>
      </div>

      <div className="max-w-screen-lg mx-auto px-4 lg:px-6 py-4 lg:py-8">
        {/* Step indicator - scrollable on mobile */}
        {currentStep !== 'success' && (
          <div className="flex items-center mb-6 lg:mb-8 overflow-x-auto scrollbar-hide pb-2">
            {displaySteps.map((step, i) => (
              <React.Fragment key={step}>
                <button
                  onClick={() => i < currentStepIndex && setCurrentStep(step)}
                  className="flex flex-col items-center gap-1.5 flex-shrink-0 touch-manipulation"
                >
                  <div className={`w-8 h-8 lg:w-10 lg:h-10 rounded-full flex items-center justify-center text-xs lg:text-sm font-rubik font-bold transition-all ${
                    currentStep === step
                      ? 'bg-[#E4191B] text-white'
                      : currentStepIndex > i
                      ? 'bg-green-500 text-white' :'bg-white border-2 border-[#D1D1D1] text-[#777777]'
                  }`}>
                    {currentStepIndex > i ? <CheckCircle size={15} /> : i + 1}
                  </div>
                  <span className={`text-[10px] lg:text-xs font-rubik whitespace-nowrap ${
                    currentStep === step ? 'text-[#E4191B] font-bold' : 'text-[#777777]'
                  }`}>
                    {STEP_LABELS[step]}
                  </span>
                </button>
                {i < displaySteps.length - 1 && (
                  <div className={`flex-1 h-px mx-1.5 lg:mx-2 min-w-[16px] ${currentStepIndex > i ? 'bg-green-500' : 'bg-[#D1D1D1]'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-7">
          {/* Main form */}
          <div className="lg:col-span-2">

            {/* ── Step: Property Details ── */}
            {currentStep === 'details' && (
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 lg:p-6">
                <h2 className="font-poppins font-medium text-[#222222] mb-4 lg:mb-5" style={{ fontSize: 'clamp(16px, 4vw, 20px)' }}>Property Details</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold font-rubik text-[#555555] mb-1.5">Listing Title *</label>
                    <input type="text" value={formData.title} onChange={(e) => update('title', e.target.value)} placeholder="e.g. 3 BHK Apartment in Whitefield" className="search-input-houzez w-full" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold font-rubik text-[#555555] mb-1.5">Property Type *</label>
                      <select value={formData.type} onChange={(e) => update('type', e.target.value)} className="search-select-houzez w-full">
                        <option value="">Select Type</option>
                        {propertyTypes.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold font-rubik text-[#555555] mb-1.5">Status *</label>
                      <select value={formData.status} onChange={(e) => update('status', e.target.value)} className="search-select-houzez w-full">
                        {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 lg:gap-4">
                    <div>
                      <label className="block text-xs font-semibold font-rubik text-[#555555] mb-1.5">Bedrooms</label>
                      <select value={formData.beds} onChange={(e) => update('beds', e.target.value)} className="search-select-houzez w-full">
                        <option value="">Select</option>
                        {['1', '2', '3', '4', '5', '6+'].map(n => <option key={n} value={n}>{n} BHK</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold font-rubik text-[#555555] mb-1.5">Bathrooms</label>
                      <select value={formData.baths} onChange={(e) => update('baths', e.target.value)} className="search-select-houzez w-full">
                        <option value="">Select</option>
                        {['1', '2', '3', '4', '5+'].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold font-rubik text-[#555555] mb-1.5">Area (sqft)</label>
                      <input type="text" value={formData.area} onChange={(e) => update('area', e.target.value)} placeholder="e.g. 1850" className="search-input-houzez w-full" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold font-rubik text-[#555555] mb-1.5">Description</label>
                    <textarea value={formData.description} onChange={(e) => update('description', e.target.value)} rows={5} placeholder="Describe your property in detail..." className="w-full border border-[#D1D1D1] rounded px-4 py-3 text-sm font-rubik text-[#222222] placeholder-gray-400 focus:outline-none focus:border-[#E4191B] transition-colors resize-none" />
                  </div>
                </div>
              </div>
            )}

            {/* ── Step: Location ── */}
            {currentStep === 'location' && (
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 lg:p-6">
                <h2 className="font-poppins font-medium text-[#222222] mb-4 lg:mb-5" style={{ fontSize: 'clamp(16px, 4vw, 20px)' }}>Location</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold font-rubik text-[#555555] mb-1.5">Full Address *</label>
                    <input type="text" value={formData.address} onChange={(e) => update('address', e.target.value)} placeholder="Street address" className="search-input-houzez w-full" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold font-rubik text-[#555555] mb-1.5">City *</label>
                      <select value={formData.city} onChange={(e) => update('city', e.target.value)} className="search-select-houzez w-full">
                        {['Bengaluru', 'Mysuru', 'Hubli', 'Mangaluru', 'Hosur', 'Tumkur'].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold font-rubik text-[#555555] mb-1.5">Pincode</label>
                      <input type="text" placeholder="e.g. 560066" className="search-input-houzez w-full" />
                    </div>
                  </div>
                  <div className="w-full rounded overflow-hidden border border-[#E5E7EB]" style={{ height: '200px' }}>
                    <div className="w-full h-full bg-[#E8EDF2] flex items-center justify-center">
                      <div className="text-center">
                        <MapPin size={24} className="text-[#E4191B] mx-auto mb-2" />
                        <p className="font-rubik text-[#777777] text-sm">Click to set location on map</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Step: Photos ── */}
            {currentStep === 'photos' && (
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 lg:p-6">
                <h2 className="font-poppins font-medium text-[#222222] mb-4 lg:mb-5" style={{ fontSize: 'clamp(16px, 4vw, 20px)' }}>Photos</h2>
                <div className="border-2 border-dashed border-[#D1D1D1] rounded-lg p-12 text-center hover:border-[#E4191B] transition-colors cursor-pointer">
                  <Upload size={32} className="text-[#D1D1D1] mx-auto mb-3" />
                  <p className="font-poppins font-medium text-[#222222] mb-1">Drop photos here or click to upload</p>
                  <p className="font-rubik text-[#777777] text-sm" style={{ fontWeight: 300 }}>Supports JPG, PNG up to 10MB each. Max 20 photos.</p>
                  <button className="mt-4 btn-houzez-secondary text-sm">Browse Files</button>
                </div>
              </div>
            )}

            {/* ── Step: Pricing ── */}
            {currentStep === 'pricing' && (
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 lg:p-6">
                <h2 className="font-poppins font-medium text-[#222222] mb-4 lg:mb-5" style={{ fontSize: 'clamp(16px, 4vw, 20px)' }}>Pricing</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold font-rubik text-[#555555] mb-1.5">Price (₹) *</label>
                    <input type="text" value={formData.price} onChange={(e) => update('price', e.target.value)} placeholder="e.g. 1,25,00,000" className="search-input-houzez w-full" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold font-rubik text-[#555555] mb-1.5">Price per sqft</label>
                      <input type="text" placeholder="Auto-calculated" className="search-input-houzez w-full bg-gray-50" readOnly />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold font-rubik text-[#555555] mb-1.5">Negotiable</label>
                      <select className="search-select-houzez w-full">
                        <option>Yes</option>
                        <option>No</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Step: Choose Plan ── */}
            {currentStep === 'plan' && (
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 lg:p-6">
                <h2 className="font-poppins font-medium text-[#222222] mb-2" style={{ fontSize: 'clamp(16px, 4vw, 20px)' }}>Choose Your Listing Plan</h2>
                <p className="font-rubik text-[#777777] text-sm mb-6" style={{ fontWeight: 300 }}>Select a plan that best suits your needs. Free listings go directly to admin review.</p>

                <div className="space-y-4">
                  {PLANS.map((plan) => (
                    <button
                      key={plan.id}
                      onClick={() => setSelectedPlan(plan)}
                      className={`w-full text-left border-2 rounded-xl p-5 transition-all relative ${
                        selectedPlan?.id === plan.id
                          ? 'border-[#E4191B] bg-[#FFF5F5]'
                          : 'border-[#E5E7EB] hover:border-[#E4191B]/40 bg-white'
                      }`}
                    >
                      {plan.badge && (
                        <span className="absolute top-3 right-3 bg-[#E4191B] text-white text-xs font-bold px-2.5 py-1 rounded-full">
                          {plan.badge}
                        </span>
                      )}
                      <div className="flex items-start gap-4">
                        <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${
                          selectedPlan?.id === plan.id ? 'border-[#E4191B] bg-[#E4191B]' : 'border-[#D1D1D1]'
                        }`}>
                          {selectedPlan?.id === plan.id && <div className="w-2 h-2 rounded-full bg-white" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <span className="font-poppins font-semibold text-[#222222]">{plan.name}</span>
                            {plan.amount === 0 ? (
                              <span className="text-green-600 font-bold text-sm">FREE</span>
                            ) : (
                              <span className="text-[#E4191B] font-bold text-lg">₹{plan.amount}</span>
                            )}
                          </div>
                          <p className="font-rubik text-[#777777] text-sm mb-3">{plan.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {plan.features.map((f, i) => (
                              <span key={i} className="flex items-center gap-1 text-xs font-rubik text-[#555555]">
                                <CheckCircle size={12} className="text-green-500 flex-shrink-0" />
                                {f}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {paymentError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 font-rubik">
                    {paymentError}
                  </div>
                )}

                <button
                  onClick={handlePlanContinue}
                  disabled={!selectedPlan || isSubmitting}
                  className="mt-6 btn-houzez-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</span>
                  ) : selectedPlan?.amount === 0 ? (
                    <><CheckCircle size={16} /> Submit Free Listing</>
                  ) : (
                    <><CreditCard size={16} /> Continue to Payment — ₹{selectedPlan?.amount}</>
                  )}
                </button>
              </div>
            )}

            {/* ── Step: Payment ── */}
            {currentStep === 'payment' && selectedPlan && (
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 lg:p-6">
                <h2 className="font-poppins font-medium text-[#222222] mb-2" style={{ fontSize: 'clamp(16px, 4vw, 20px)' }}>Complete Payment</h2>
                <p className="font-rubik text-[#777777] text-sm mb-6" style={{ fontWeight: 300 }}>Secure payment powered by Razorpay. Supports UPI, cards, and net banking.</p>

                {/* Order summary */}
                <div className="bg-[#F8F8F8] rounded-xl p-5 mb-6">
                  <h3 className="font-rubik font-semibold text-[#222222] text-sm mb-3">Order Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm font-rubik">
                      <span className="text-[#777777]">Plan</span>
                      <span className="text-[#222222] font-medium">{selectedPlan.name}</span>
                    </div>
                    <div className="flex justify-between text-sm font-rubik">
                      <span className="text-[#777777]">Duration</span>
                      <span className="text-[#222222]">{selectedPlan.duration_days} days</span>
                    </div>
                    <div className="flex justify-between text-sm font-rubik">
                      <span className="text-[#777777]">Listing</span>
                      <span className="text-[#222222] truncate max-w-[180px]">{formData.title || 'Your Listing'}</span>
                    </div>
                    <div className="border-t border-[#E5E7EB] pt-2 mt-2 flex justify-between font-rubik font-bold">
                      <span className="text-[#222222]">Total</span>
                      <span className="text-[#E4191B] text-lg">₹{selectedPlan.amount}</span>
                    </div>
                  </div>
                </div>

                {/* Payment methods */}
                <div className="flex items-center gap-3 mb-6 flex-wrap">
                  {[
                    { icon: '💳', label: 'Cards' },
                    { icon: '📱', label: 'UPI' },
                    { icon: '🏦', label: 'Net Banking' },
                    { icon: '💰', label: 'Wallets' },
                  ].map((m) => (
                    <div key={m.label} className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-rubik text-[#555555]">
                      <span>{m.icon}</span>
                      <span>{m.label}</span>
                    </div>
                  ))}
                </div>

                {/* Test card info */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <p className="text-xs font-semibold text-amber-800 mb-1">🧪 Test Mode — Use test credentials</p>
                  <p className="text-xs text-amber-700 font-rubik">Card: <span className="font-mono font-bold">4100 2800 0000 1007</span> · Expiry: any future date · CVV: any 3 digits</p>
                </div>

                {paymentError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 font-rubik">
                    {paymentError}
                  </div>
                )}

                <button
                  onClick={handleRazorpayPayment}
                  disabled={isSubmitting || !razorpayScriptLoaded}
                  className="btn-houzez-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</span>
                  ) : (
                    <><Shield size={16} /> Pay ₹{selectedPlan.amount} Securely</>
                  )}
                </button>

                <p className="text-center text-xs text-[#999999] font-rubik mt-3">
                  🔒 256-bit SSL encrypted · Powered by Razorpay
                </p>
              </div>
            )}

            {/* ── Step: Success ── */}
            {currentStep === 'success' && (
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-500" />
                </div>
                <h2 className="font-poppins font-semibold text-[#222222] text-2xl mb-2">
                  {selectedPlan?.amount === 0 ? 'Listing Submitted!' : 'Payment Successful!'}
                </h2>
                <p className="font-rubik text-[#777777] mb-6">
                  {selectedPlan?.amount === 0
                    ? 'Your free listing has been submitted and is pending admin approval. You\'ll be notified once it goes live.'
                    : 'Your payment was successful. Your listing has been submitted for admin approval and will go live shortly.'}
                </p>

                <div className="bg-[#F8F8F8] rounded-xl p-4 mb-6 text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap size={14} className="text-[#E4191B]" />
                    <span className="font-rubik font-semibold text-[#222222] text-sm">What happens next?</span>
                  </div>
                  <ul className="space-y-1.5">
                    {[
                      'Our team will review your listing within 24 hours',
                      'You\'ll receive an email/SMS once approved',
                      'Your listing will appear in search results',
                      'Buyers can contact you directly',
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm font-rubik text-[#555555]">
                        <CheckCircle size={12} className="text-green-500 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link href="/seller-dashboard" className="btn-houzez-primary flex items-center justify-center gap-2">
                    View My Dashboard <ArrowRight size={14} />
                  </Link>
                  <Link href="/" className="btn-houzez-secondary flex items-center justify-center gap-2">
                    Back to Home
                  </Link>
                </div>
              </div>
            )}

            {/* Navigation buttons */}
            {currentStep !== 'success' && currentStep !== 'plan' && currentStep !== 'payment' && (
              <div className="flex items-center justify-between mt-5">
                <button
                  onClick={goPrev}
                  disabled={currentStepIndex === 0}
                  className={`px-6 py-3 rounded border text-sm font-semibold font-rubik transition-all ${
                    currentStepIndex === 0
                      ? 'border-[#D1D1D1] text-[#D1D1D1] cursor-not-allowed'
                      : 'border-[#E4191B] text-[#E4191B] hover:bg-[#E4191B] hover:text-white'
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={goNext}
                  className="btn-houzez-primary flex items-center gap-2"
                >
                  Next Step <ArrowRight size={14} />
                </button>
              </div>
            )}

            {currentStep === 'payment' && (
              <div className="flex items-center justify-between mt-5">
                <button
                  onClick={() => setCurrentStep('plan')}
                  className="px-6 py-3 rounded border border-[#E4191B] text-[#E4191B] text-sm font-semibold font-rubik hover:bg-[#E4191B] hover:text-white transition-all"
                >
                  ← Change Plan
                </button>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 sticky top-24">
              <h3 className="font-poppins font-medium text-[#222222] mb-4" style={{ fontSize: '16px' }}>Listing Tips</h3>
              <div className="space-y-3">
                {[
                  { id: 'tip-1', tip: 'Add at least 5 high-quality photos to get 3x more enquiries.' },
                  { id: 'tip-2', tip: 'Include RERA number and EC details to build buyer trust.' },
                  { id: 'tip-3', tip: 'Set a competitive price by checking similar listings in your area.' },
                  { id: 'tip-4', tip: 'Write a detailed description highlighting unique features.' },
                ].map((item) => (
                  <div key={item.id} className="flex items-start gap-2.5">
                    <CheckCircle size={14} className="text-[#E4191B] flex-shrink-0 mt-0.5" />
                    <p className="font-rubik text-[#555555] text-sm" style={{ fontWeight: 300 }}>{item.tip}</p>
                  </div>
                ))}
              </div>

              {selectedPlan && currentStep !== 'success' && (
                <div className="mt-6 p-4 bg-[#FFF1F0] rounded border border-[#FECACA]">
                  <p className="font-poppins font-semibold text-[#E4191B] text-sm mb-1">
                    {selectedPlan.amount === 0 ? '✓ Free Listing' : `✓ ${selectedPlan.name}`}
                  </p>
                  <p className="font-rubik text-[#555555] text-xs" style={{ fontWeight: 300 }}>
                    {selectedPlan.amount === 0
                      ? 'Your listing will be submitted for free after admin review.'
                      : `₹${selectedPlan.amount} · ${selectedPlan.duration_days} days · Premium visibility`}
                  </p>
                </div>
              )}

              {!selectedPlan && (
                <div className="mt-6 p-4 bg-[#FFF1F0] rounded border border-[#FECACA]">
                  <p className="font-poppins font-semibold text-[#E4191B] text-sm mb-1">Free Listing Available</p>
                  <p className="font-rubik text-[#555555] text-xs" style={{ fontWeight: 300 }}>Post your first listing for free. Upgrade to Featured for maximum visibility.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}